import { makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import BaseEmployeeStore from '../../../../shared/stores/baseEmployeeStore';
import { OrderItem, OrderItemApi } from '../models/OrderItem';
import { OrderNewStore } from './OrderNewStore';
import OrderViewStore from './OrderViewStore';

export enum OrderOverviewState {
    Overview = 0,
    New = 1,
    View = 2,
}

class OrderOverviewStore extends BaseEmployeeStore {
    public orderList: OrderItem[] = [];
    public currentWindowStore: OrderNewStore | OrderViewStore | null = null;
    public state: OrderOverviewState = OrderOverviewState.Overview;

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            orderList: observable,
            currentWindowStore: observable,
            state: observable,
        });
    }

    get isBackButtonVisible(): boolean {
        return true;
    }

    get isFooterButtonVisible(): boolean {
        if (this.currentWindowStore) {
            return this.currentWindowStore.isFooterButtonVisible;
        }
        return true;
    }

    get isFooterButtonEnabled(): boolean {
        if (this.currentWindowStore) {
            return this.currentWindowStore.isFooterButtonEnabled;
        }
        return true;
    }

    get isFooterButtonLoading(): boolean {
        if (this.currentWindowStore) {
            return this.currentWindowStore.isFooterButtonLoading;
        }
        return this.isLoading;
    }

    get footerButtonHelpText(): string {
        if (this.currentWindowStore) {
            return this.currentWindowStore.footerButtonHelpText;
        }
        return '';
    }

    public async onBeforeNavigateBack(): Promise<boolean> {
        if (this.currentWindowStore) {
            return this.currentWindowStore.onBeforeNavigateBack();
        }
        return !this.isLoading;
    }

    public async onInit(): Promise<void> {
        await this.openOverviewOrder(true);
    }

    public async openNewOrder(): Promise<void> {
        runInAction(() => {
            this.state = OrderOverviewState.New;
            this.currentWindowStore = new OrderNewStore(this.sdk, this);
            this.currentWindowStore.init();
        });
    }

    public async openViewOrder(orderId: number): Promise<void> {
        runInAction(() => {
            this.state = OrderOverviewState.View;
            this.currentWindowStore = new OrderViewStore(this.sdk, this);
            this.currentWindowStore.orderId = orderId;
            this.currentWindowStore.init();
        });
    }

    public async openOverviewOrder(refreshData: boolean): Promise<void> {
        runInAction(() => {
            this.state = OrderOverviewState.Overview;
            this.currentWindowStore = null;
        });

        this.updateFooterButton({
            label: 'Neue Bestellung',
            icon: 'pi pi-truck',
            onClick: () => this.openNewOrder(),
        });

        const menuItem = this.sdk.findMenuItemByKey('neworder');
        if (menuItem) {
            this.setMenuConfig({
                customHeader: {
                    title: 'Bestellungen',
                    icon: 'pi pi-truck',
                },
                backButton: {
                    visible: true,
                    disabled: false,
                    customAction: undefined,
                },
            });
        }

        if (refreshData) {
            await this.fetchOrders();
        }
    }

    public async fetchOrders(): Promise<void> {
        this.setLoading(true);

        const orderListResult = await this.sdk.fetchApi('order', 'employee/query', {
            limit: 10,
        }); // Todo: If we want to have pagination, we need to pass page and limit here

        this.setLoading(false);

        runInAction(() => {
            if (orderListResult?.status === 200) {
                const orderItemApiList: OrderItemApi[] = orderListResult.json.data;
                this.orderList = orderItemApiList.map<OrderItem>((orderItemApi: OrderItemApi) => {
                    return {
                        id: orderItemApi.id,
                        objectId: orderItemApi.object_id,
                        objectNameManual: orderItemApi.object_name_manual,
                        objectName: orderItemApi.object_name,
                        employeeId: orderItemApi.employee_id,
                        delieveredUtc: orderItemApi.delievered_utc,
                        createdUtc: orderItemApi.created_utc,
                        updatedUtc: orderItemApi.updated_utc,
                    };
                });
            }
        });
    }
}

export default OrderOverviewStore;
